import { useState, useRef, useCallback, useEffect } from 'react';

// Formatos por orden de preferencia. Chrome en Android graba webm/opus, que es
// pequeno y se reproduce en cualquier Chrome; mp4 queda para Safari.
const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
export const MAX_AUDIO_SECONDS = 5 * 60;
const PERMISSION_TIMEOUT_MS = 8000;
const DENIED_MESSAGE = 'Sin permiso para el microfono. Activalo en Ajustes del telefono > '
  + 'Aplicaciones > Chrome > Permisos > Microfono.';

const pickMime = () => {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return '';
  return MIME_CANDIDATES.find((m) => MediaRecorder.isTypeSupported(m)) || '';
};

/**
 * Grabacion de notas de voz con MediaRecorder.
 *
 * `start()` pide el microfono (la primera vez el sistema pregunta permiso),
 * `stop()` resuelve con { blob, duration, mimeType } y `cancel()` lo descarta.
 * Quien lo usa debe enviar la grabacion al llegar a MAX_AUDIO_SECONDS (5
 * minutos), para que nadie suba media hora de audio por descuido.
 */
export default function useAudioRecorder() {
  const [recording, setRecording] = useState(false);
  const [starting, setStarting] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState('');
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const startedAtRef = useRef(0);
  const resolveRef = useRef(null);

  const cleanup = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    recorderRef.current = null;
    setRecording(false);
    setSeconds(0);
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const stop = useCallback(() => new Promise((resolve) => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      resolve(null);
      return;
    }
    resolveRef.current = resolve;
    recorder.stop();
  }), []);

  const start = useCallback(async () => {
    setError('');
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('Este navegador no permite grabar audio aqui.');
      return false;
    }
    // Se marca al instante: pedir el microfono puede tardar (dialogo del
    // sistema) y el boton tiene que responder ya.
    setStarting(true);
    try {
      // Si el permiso ya esta denegado, getUserMedia fallaria o, en la app
      // instalada, podria quedarse esperando sin mostrar nada: se dice antes.
      try {
        const status = await navigator.permissions?.query({ name: 'microphone' });
        if (status?.state === 'denied') {
          setError(DENIED_MESSAGE);
          return false;
        }
      } catch (e) {
        // Navegadores que no aceptan consultar 'microphone': se sigue igual
      }

      // En la app instalada (TWA) se ha visto que la peticion del microfono
      // puede quedarse colgada sin dialogo ni error. Con el limite, al menos se
      // avisa y se ofrece la grabadora del movil.
      const request = navigator.mediaDevices.getUserMedia({ audio: true });
      let timedOut = false;
      // Si el permiso llega despues del limite, se suelta el microfono
      request.then((late) => {
        if (timedOut) late.getTracks().forEach((t) => t.stop());
      }).catch(() => {});
      const stream = await Promise.race([
        request,
        new Promise((_, reject) => setTimeout(() => {
          timedOut = true;
          reject(Object.assign(new Error('timeout'), { name: 'TimeoutError' }));
        }, PERMISSION_TIMEOUT_MS)),
      ]);
      streamRef.current = stream;
      const mimeType = pickMime();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const duration = (Date.now() - startedAtRef.current) / 1000;
        const type = recorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type });
        cleanup();
        if (resolveRef.current) {
          resolveRef.current({ blob, duration, mimeType: type });
          resolveRef.current = null;
        }
      };
      recorderRef.current = recorder;
      startedAtRef.current = Date.now();
      recorder.start(1000);
      setRecording(true);
      timerRef.current = setInterval(() => {
        setSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000));
      }, 250);
      return true;
    } catch (err) {
      cleanup();
      if (err.name === 'NotAllowedError' || err.name === 'SecurityError') setError(DENIED_MESSAGE);
      else if (err.name === 'TimeoutError') setError('El movil no ha dado acceso al microfono.');
      else if (err.name === 'NotFoundError') setError('No se encuentra ningun microfono.');
      else setError(`No se ha podido empezar a grabar (${err.name || err.message}).`);
      return false;
    } finally {
      setStarting(false);
    }
  }, [cleanup]);

  const cancel = useCallback(() => {
    resolveRef.current = null;
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.onstop = () => cleanup();
      recorder.stop();
    } else {
      cleanup();
    }
  }, [cleanup]);

  return { recording, starting, seconds, error, setError, start, stop, cancel };
}
